import { useMemo, useState } from 'react';
import { Pencil, Plus, Trash2, Users } from 'lucide-react';
import { LiksButton } from '../../components/liks/LiksButton/LiksButton';
import { LiksSearch } from '../../components/liks/LiksSearch/LiksSearch';
import {
  useAssignTenantUserGroupMutation,
  useCreateTenantGroupMutation,
  useDeleteTenantGroupMutation,
  useRenameTenantGroupMutation,
  useUnassignTenantUserGroupMutation,
} from '../../features/tenant/mutations/useTenantGroupMutations';
import {
  useTenantGroupMembersQueries,
  useTenantGroupMembersQuery,
} from '../../features/tenant/queries/useTenantGroupMembersQuery';
import { useTenantGroupsQuery } from '../../features/tenant/queries/useTenantGroupsQuery';
import { useTenantUsersQuery } from '../../features/tenant/queries/useTenantUsersQuery';
import { useI18n } from '../../i18n/useI18n';
import type { WithResourceId } from '../../types/knowledge';
import type { UserGroup } from '../../types/tenant';
import type { TenantAssignableUser } from './TenantResourceUserAssignments';
import { TenantGroupsSectionDialogs } from './TenantGroupsSectionDialogs';
import './TenantLabelsGroupsSection.css';

type NamedGroup = WithResourceId<UserGroup> & { name: string };

export function TenantGroupsSection() {
  const { translate } = useI18n();
  const groupsQuery = useTenantGroupsQuery();
  const usersQuery = useTenantUsersQuery();
  const createMutation = useCreateTenantGroupMutation();
  const renameMutation = useRenameTenantGroupMutation();
  const deleteMutation = useDeleteTenantGroupMutation();
  const assignMutation = useAssignTenantUserGroupMutation();
  const unassignMutation = useUnassignTenantUserGroupMutation();

  const [search, setSearch] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState<NamedGroup | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<NamedGroup | null>(null);
  const [manageTarget, setManageTarget] = useState<NamedGroup | null>(null);
  const [pendingUserId, setPendingUserId] = useState<string | null>(null);

  const directoryUsers = useMemo(
    () =>
      (usersQuery.data ?? []).filter(
        (user): user is TenantAssignableUser =>
          typeof user.id === 'string' && user.id.length > 0,
      ),
    [usersQuery.data],
  );

  const groups = useMemo(() => {
    const query = search.trim().toLowerCase();
    const list = [...(groupsQuery.data ?? [])].sort((a, b) =>
      a.name.localeCompare(b.name),
    );
    if (query === '') {
      return list;
    }
    return list.filter((group) => group.name.toLowerCase().includes(query));
  }, [groupsQuery.data, search]);

  const groupIds = useMemo(() => groups.map((group) => group.id), [groups]);
  const groupMembersQueries = useTenantGroupMembersQueries(
    groupIds,
    !groupsQuery.isLoading && !groupsQuery.isError,
  );

  const userCountByGroupId = useMemo(() => {
    const map = new Map<string, number>();
    for (let index = 0; index < groupIds.length; index += 1) {
      const groupId = groupIds[index];
      const users = groupMembersQueries[index]?.data;
      if (groupId && users) {
        map.set(groupId, users.length);
      }
    }
    return map;
  }, [groupIds, groupMembersQueries]);

  const manageUsersQuery = useTenantGroupMembersQuery(manageTarget?.id);

  const formatUserCount = (count: number | undefined) => {
    if (count == null) {
      return translate(
        'organisationManagement.labelsAndGroups.userCountLoading',
      );
    }
    return translate('organisationManagement.labelsAndGroups.userCount', {
      count: String(count),
    });
  };

  return (
    <section className="tenant-labels-groups__section">
      <div className="tenant-labels-groups__section-header">
        <div>
          <h2 className="tenant-labels-groups__section-title">
            {translate('organisationManagement.labelsAndGroups.groupsTitle')}
          </h2>
          <p className="tenant-labels-groups__section-description">
            {translate(
              'organisationManagement.labelsAndGroups.groupsDescription',
            )}
          </p>
        </div>
        <LiksButton type="button" onClick={() => setIsCreateOpen(true)}>
          <Plus strokeWidth={2} aria-hidden />
          {translate('organisationManagement.labelsAndGroups.createGroup')}
        </LiksButton>
      </div>

      <div className="tenant-labels-groups__toolbar">
        <LiksSearch
          className="tenant-labels-groups__search"
          value={search}
          onChange={setSearch}
          placeholder={translate(
            'organisationManagement.labelsAndGroups.searchGroupsPlaceholder',
          )}
          aria-label={translate(
            'organisationManagement.labelsAndGroups.searchGroupsLabel',
          )}
          clearLabel={translate('common.search.clear')}
          variant="on-light"
        />
      </div>

      {groupsQuery.isLoading ? (
        <p className="tenant-labels-groups__status">
          {translate('organisationManagement.labelsAndGroups.groupsLoading')}
        </p>
      ) : null}

      {groupsQuery.isError ? (
        <p className="tenant-labels-groups__status" role="alert">
          {translate('organisationManagement.labelsAndGroups.groupsLoadError')}
        </p>
      ) : null}

      {!groupsQuery.isLoading && !groupsQuery.isError && groups.length === 0 ? (
        <p className="tenant-labels-groups__empty">
          {search.trim()
            ? translate(
                'organisationManagement.labelsAndGroups.groupsNoResults',
              )
            : translate('organisationManagement.labelsAndGroups.groupsEmpty')}
        </p>
      ) : null}

      {groups.length > 0 ? (
        <ul className="tenant-labels-groups__list">
          {groups.map((group) => (
            <li key={group.id} className="tenant-labels-groups__item">
              <div className="tenant-labels-groups__item-row">
                <div className="tenant-labels-groups__item-main">
                  <span className="tenant-labels-groups__chip tenant-labels-groups__chip--group">
                    {group.name}
                  </span>
                  <span className="tenant-labels-groups__user-count">
                    {formatUserCount(userCountByGroupId.get(group.id))}
                  </span>
                </div>
                <div className="tenant-labels-groups__item-actions">
                  <LiksButton
                    type="button"
                    variant="secondary"
                    onClick={() => setManageTarget(group)}
                    aria-label={translate(
                      'organisationManagement.labelsAndGroups.manageUsersFor',
                      { name: group.name },
                    )}
                  >
                    <Users strokeWidth={2} aria-hidden />
                    {translate(
                      'organisationManagement.labelsAndGroups.manageUsers',
                    )}
                  </LiksButton>
                  <LiksButton
                    type="button"
                    variant="secondary"
                    onClick={() => setRenameTarget(group)}
                    aria-label={translate(
                      'organisationManagement.labelsAndGroups.renameGroup',
                      { name: group.name },
                    )}
                  >
                    <Pencil strokeWidth={2} aria-hidden />
                    {translate('organisationManagement.labelsAndGroups.rename')}
                  </LiksButton>
                  <LiksButton
                    type="button"
                    variant="danger"
                    onClick={() => setDeleteTarget(group)}
                    aria-label={translate(
                      'organisationManagement.labelsAndGroups.deleteGroup',
                      { name: group.name },
                    )}
                  >
                    <Trash2 strokeWidth={2} aria-hidden />
                    {translate('organisationManagement.labelsAndGroups.delete')}
                  </LiksButton>
                </div>
              </div>
            </li>
          ))}
        </ul>
      ) : null}

      <TenantGroupsSectionDialogs
        isCreateOpen={isCreateOpen}
        onCloseCreate={() => setIsCreateOpen(false)}
        renameTarget={renameTarget}
        onCloseRename={() => setRenameTarget(null)}
        deleteTarget={deleteTarget}
        onCloseDelete={() => setDeleteTarget(null)}
        manageTarget={manageTarget}
        onCloseManage={() => setManageTarget(null)}
        directoryUsers={directoryUsers}
        assignedUsers={manageUsersQuery.data ?? []}
        isManageLoading={manageUsersQuery.isLoading}
        isManageError={manageUsersQuery.isError}
        pendingUserId={pendingUserId}
        setPendingUserId={setPendingUserId}
        createMutation={createMutation}
        renameMutation={renameMutation}
        deleteMutation={deleteMutation}
        assignMutation={assignMutation}
        unassignMutation={unassignMutation}
      />
    </section>
  );
}
